import { useMutation, useQueryClient } from '@northladder/services';
import { IApiError } from '@northladder/utilities';

import { CUSTOMER_SERVICE_API } from '../customerServiceClient';
import { IUpdateGradeChangeRequestResponse } from '../types';

import { DEAL_DETAILS_QUERY } from './useDealDetailsQuery';
import { FETCH_GRADE_REVIEW_REQUEST_QUERY } from './useGradeReviewRequestListQuery';
import { GRADE_REVIEW_REQUEST_DETAILS_QUERY } from './useGradeReviewRequestDetailsQuery';

/**
 * This is to track the update grade change request mutation keys in react query cache.
 */
const MUT_UPDATE_GRADE_CHANGE_REQUEST_KEY =
  'update-grade-change-request-mutation-key';

interface ICreateGradeChangeRequest {
  onError?: (err: IApiError) => void;
  /**
   * Callback to be called when the change grade request successfully created
   */
  onSuccessCallback?: (
    createGradeChangeResponse: IUpdateGradeChangeRequestResponse
  ) => void;
  onSuccessEvent?: (event: string) => void;
}

/**
 * This performs a post request for uploading a file.
 */
export const useUpdateGradeChangeRequestMutation = (
  options?: ICreateGradeChangeRequest
) => {
  const { onError, onSuccessCallback, onSuccessEvent } = options || {};

  const queryClient = useQueryClient();
  const { isPending, isError, error, mutate } = useMutation({
    mutationKey: [MUT_UPDATE_GRADE_CHANGE_REQUEST_KEY],
    networkMode: 'always', // Even make calls when offline
    mutationFn: CUSTOMER_SERVICE_API.updateGradeChangeRequest,
    onSuccess: (response, variable) => {
      onSuccessCallback?.(response);
      onSuccessEvent?.(variable.event || '');

      queryClient.invalidateQueries({
        queryKey: [FETCH_GRADE_REVIEW_REQUEST_QUERY],
      });

      queryClient.invalidateQueries({
        queryKey: [GRADE_REVIEW_REQUEST_DETAILS_QUERY],
      });

      queryClient.invalidateQueries({
        queryKey: [DEAL_DETAILS_QUERY],
      });
    },
    onError,
  });

  return {
    error,
    isPending,
    isError,
    mutate,
  };
};
